import React, { useState, useEffect, useCallback } from 'react';
import PageExplainer from '@/components/PageExplainer';
import { useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import Navigation from '@/components/Navigation';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import { NewsHeadlines, SuggestedFollows } from '@/components/dashboard/DashboardRailWidgets';
import QuickActions from '@/components/QuickActions';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Search, FileText, Upload, Download, BookOpen, Briefcase, Clock,
  ChevronLeft, ChevronRight, Filter, X, Tag, User, CheckCircle, AlertCircle,
  Bookmark, BookmarkCheck, Quote, Copy, Star, MessageSquare, Eye, Send,
  ExternalLink, Pencil, Sparkles, FileSearch
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Trigger a file download via fetch-to-blob (reliable, no iframe/popup issues) */
async function triggerDownload(url, filename) {
  try {
    const token = sessionStorage.getItem('session_token');
    const res = await fetch(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 100);
  } catch (err) {
    console.error('Download error:', err);
    toast.error('Download failed. Please try again.');
  }
}

/** Open a document in a new browser tab via fetch-to-blob */
async function openInNewTab(url) {
  try {
    const token = sessionStorage.getItem('session_token');
    const res = await fetch(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Failed to load (${res.status})`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  } catch (err) {
    console.error('Open in new tab error:', err);
    toast.error('Failed to open document. Please try again.');
  }
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

/* ── Star Rating (interactive or read-only) ── */
function StarRating({ value = 0, onChange, size = 'sm', showCount, count }) {
  const [hover, setHover] = useState(0);
  const interactive = !!onChange;
  const sizeClass = size === 'sm' ? 'h-3.5 w-3.5' : size === 'md' ? 'h-5 w-5' : 'h-6 w-6';

  return (
    <span className="inline-flex items-center gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => {
        const filled = interactive ? (hover || value) >= i : value >= i;
        const half = !filled && value >= i - 0.5 && !interactive;
        return (
          <button
            key={i}
            type="button"
            disabled={!interactive}
            className={`p-0 border-0 bg-transparent ${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
            onClick={() => interactive && onChange(i)}
            onMouseEnter={() => interactive && setHover(i)}
            onMouseLeave={() => interactive && setHover(0)}
            data-testid={`star-${i}`}
          >
            <Star
              className={`${sizeClass} transition-colors ${
                filled ? 'text-amber-400 fill-amber-400' : half ? 'text-amber-400 fill-amber-400/50' : 'text-slate-300'
              }`}
            />
          </button>
        );
      })}
      {showCount && count > 0 && (
        <span className="text-xs text-slate-500 ml-1">({count})</span>
      )}
    </span>
  );
}

/* ── PDF Viewer using react-pdf (JS-based, no browser plugin needed) ── */
function PdfViewer({ blobUrl, downloadUrl, viewUrl, filename }) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useCallback(node => {
    if (node) setContainerWidth(node.getBoundingClientRect().width - 2);
  }, []);

  return (
    <div className="mb-4" data-testid="pdf-viewer">
      <div className="flex items-center justify-between mb-2">
        {numPages && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)} data-testid="pdf-prev-page">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs text-slate-500">Page {currentPage} of {numPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage >= numPages}
              onClick={() => setCurrentPage(p => p + 1)} data-testid="pdf-next-page">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="open-pdf-newtab"
            onClick={() => openInNewTab(viewUrl)}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open in New Tab
          </Button>
          <Button size="sm" className="bg-brand-navy text-white" data-testid="download-from-preview"
            onClick={() => { triggerDownload(downloadUrl, filename); }}>
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download
          </Button>
        </div>
      </div>
      <div ref={containerRef} className="rounded-lg border overflow-auto bg-slate-50" style={{ maxHeight: '500px' }}>
        <Document
          file={blobUrl}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          loading={
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin h-6 w-6 border-2 border-brand-navy border-t-transparent rounded-full" />
            </div>
          }
          error={
            <div className="text-center py-12">
              <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Unable to render PDF</p>
            </div>
          }
        >
          <Page
            pageNumber={currentPage}
            width={containerWidth}
            renderTextLayer={true}
            renderAnnotationLayer={true}
          />
        </Document>
      </div>
    </div>
  );
}

/* ── Doc Card with average star rating ── */
function DocCard({ doc, onView, onBookmark }) {
  const catColor = doc.category === 'Academic' ? 'brand-navy' : doc.category === 'Case Study' ? 'amber-600' : 'brand-sage';
  const catBg = doc.category === 'Academic' ? 'bg-brand-navy' : doc.category === 'Case Study' ? 'bg-amber-600' : 'bg-brand-sage';
  const catBgLight = doc.category === 'Academic' ? 'bg-brand-navy/8' : doc.category === 'Case Study' ? 'bg-amber-100' : 'bg-brand-sage/15';
  const catText = doc.category === 'Academic' ? 'text-brand-navy' : doc.category === 'Case Study' ? 'text-amber-700' : 'text-brand-sage';
  const CatIcon = doc.category === 'Academic' ? BookOpen : doc.category === 'Case Study' ? FileSearch : Briefcase;
  return (
    <Card className="group p-0 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer h-full flex flex-col"
      onClick={() => onView(doc)} data-testid={`research-doc-${doc.doc_id}`}>
      <div className={`h-1.5 ${catBg}`} />
      <div className="p-5 flex flex-col h-full">
        {/* Header: icon, category, title (locked to one line) */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${catBgLight}`}>
            <CatIcon className={`h-5 w-5 ${catText}`} />
          </div>
          <div className="flex-1 min-w-0">
            <span className={`text-[10px] font-bold uppercase tracking-wide ${catText}`}>{doc.category}</span>
            <h3
              className="font-bold text-slate-800 group-hover:text-brand-navy transition-colors leading-snug mt-0.5 line-clamp-2 min-h-[2.75em]"
              title={doc.title}
              data-testid={`doc-title-${doc.doc_id}`}
            >
              {doc.title}
            </h3>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onBookmark(doc); }}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid={`bookmark-btn-${doc.doc_id}`}
          >
            {doc.is_bookmarked
              ? <BookmarkCheck className="h-4.5 w-4.5 text-brand-navy fill-brand-navy" />
              : <Bookmark className="h-4.5 w-4.5 text-slate-400 hover:text-brand-navy" />
            }
          </button>
        </div>

        {/* Abstract — fixed 2-line slot so cards align across rows even if
            one card has a one-line summary. */}
        <div className="text-sm text-slate-600 leading-relaxed mb-3 min-h-[40px]">
          {doc.abstract ? <RichText html={doc.abstract} className="card card-2" /> : <span className="text-slate-300">No abstract provided.</span>}
        </div>

        {/* Rating slot — always reserved (24px) regardless of whether
            this doc has been rated, so the metadata row below aligns
            horizontally across cards. */}
        <div className="min-h-[24px] mb-3" data-testid={`card-rating-slot-${doc.doc_id}`}>
          {doc.avg_rating > 0 && (
            <div className="flex items-center gap-1.5" data-testid={`card-rating-${doc.doc_id}`}>
              <StarRating value={doc.avg_rating} size="sm" showCount count={doc.rating_count} />
              <span className="text-xs font-semibold text-amber-600">{doc.avg_rating}</span>
            </div>
          )}
        </div>

        {/* Authors slot — separate fixed-height row so missing authors
            don't push the metadata up against the abstract. */}
        <div className="text-xs text-slate-400 truncate min-h-[18px] mb-1.5" data-testid={`card-authors-${doc.doc_id}`}>
          {doc.authors ? (
            <span className="flex items-center gap-1 truncate"><User className="h-3 w-3 flex-shrink-0" />{doc.authors}</span>
          ) : null}
        </div>

        {/* Metadata row (institution / country / date / size / reads /
            citations). Reserved height keeps tags aligned across cards. */}
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 flex-wrap min-h-[18px]">
          {doc.institution && <span className="truncate">{doc.institution}</span>}
          {doc.country && <span>{doc.country}</span>}
          {(doc.month || doc.year) && <span>{[doc.month, doc.year].filter(Boolean).join(' ')}</span>}
          {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
          {(doc.citation_count > 0) && (
            <span className="flex items-center gap-1 text-brand-navy font-medium" data-testid={`citation-count-${doc.doc_id}`}>
              <Quote className="h-3 w-3" />{doc.citation_count}
            </span>
          )}
          {(doc.read_count > 0) && (
            <span className="flex items-center gap-1 text-slate-500 font-medium" data-testid={`read-count-${doc.doc_id}`}>
              <Eye className="h-3 w-3" />{doc.read_count} read{doc.read_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Tags pinned to the bottom so cards in the same row line up
            their tag strips regardless of how much body content sits
            above them. */}
        <div className="mt-auto flex flex-wrap gap-1.5 min-h-[22px]">
          {doc.tags?.length > 0 && (
            <>
              {doc.tags.slice(0, 3).map(t => (
                <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{t}</span>
              ))}
              {doc.tags.length > 3 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-400">+{doc.tags.length - 3}</span>}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ── Research Detail Dialog ── */
function ResearchDetail({ doc, user, token, headers, fetchOpts, onClose, onBookmark, onCite, onRated, onUpdated }) {
  const [activeTab, setActiveTab] = useState('preview');
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userRating, setUserRating] = useState(doc.user_rating || 0);
  const [avgRating, setAvgRating] = useState(doc.avg_rating || 0);
  const [ratingCount, setRatingCount] = useState(doc.rating_count || 0);
  const [docDetail, setDocDetail] = useState(doc);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allTags] = useState([
    "Sustainability", "Supply Chain", "Logistics", "ESG", "Net Zero",
    "Circular Economy", "Green Technology", "Regulation & Compliance",
    "Innovation", "Leadership", "Diversity & Inclusion", "Data & Analytics",
    "Procurement", "Warehousing", "Last Mile Delivery", "EV & Fleet",
    "Carbon Reporting", "Scope 3 Emissions", "AI & Automation",
    "Renewable Energy", "Biodiversity", "Water Stewardship", "Social Impact",
    "Sustainable Packaging", "Green Logistics", "Waste Reduction",
  ]);

  const isPdf = docDetail.content_type === 'application/pdf' || docDetail.original_filename?.toLowerCase().endsWith('.pdf');
  const isOwner = user && docDetail.uploaded_by === user.user_id;
  const isAdmin = user && user.role === 'admin';
  const canEdit = isOwner || isAdmin;

  // Fetch full doc detail (with user_rating)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/learning/${doc.doc_id}`, fetchOpts);
        if (res.ok) {
          const d = await res.json();
          setDocDetail(d);
          setUserRating(d.user_rating || 0);
          setAvgRating(d.avg_rating || 0);
          setRatingCount(d.rating_count || 0);
        }
      } catch {}
    })();
  }, [doc.doc_id]);

  // Fetch document blob for preview
  useEffect(() => {
    if (!isPdf) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(false);
    (async () => {
      try {
        const t = sessionStorage.getItem('session_token');
        const res = await fetch(`${BACKEND_URL}/api/learning/${doc.doc_id}/view${t ? `?auth=${t}` : ''}`, {
          credentials: 'include',
          headers: { ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        });
        if (!res.ok) throw new Error('Failed');
        const arrayBuf = await res.arrayBuffer();
        if (cancelled) return;
        // Explicitly create blob with PDF MIME type
        const blob = new Blob([arrayBuf], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPreviewBlobUrl(url);
      } catch {
        if (!cancelled) setPreviewError(true);
      }
      if (!cancelled) setPreviewLoading(false);
    })();
    return () => { cancelled = true; };
  }, [doc.doc_id, isPdf]);

  // Load comments
  useEffect(() => {
    loadComments();
  }, [doc.doc_id]);

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/learning/${doc.doc_id}/comments`, fetchOpts);
      if (res.ok) {
        const d = await res.json();
        setComments(d.comments || []);
      }
    } catch {}
    setLoadingComments(false);
  };

  const handleRate = async (rating) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/learning/${doc.doc_id}/rate`, {
        method: 'POST',
        ...fetchOpts,
        body: JSON.stringify({ rating }),
      });
      if (res.ok) {
        const d = await res.json();
        setUserRating(d.user_rating);
        setAvgRating(d.avg_rating);
        setRatingCount(d.rating_count);
        onRated(doc.doc_id, d.avg_rating, d.rating_count);
        toast.success(`Rated ${rating} star${rating > 1 ? 's' : ''}`);
      }
    } catch {
      toast.error('Failed to rate');
    }
  };

  const handleComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/learning/${doc.doc_id}/comments`, {
        method: 'POST',
        ...fetchOpts,
        body: JSON.stringify({ text }),
      });
      if (res.ok) {
        const c = await res.json();
        setComments(prev => [c, ...prev]);
        setCommentText('');
        toast.success('Comment added');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to comment');
      }
    } catch {
      toast.error('Failed to comment');
    }
    setSubmitting(false);
  };

  const startEdit = () => {
    setEditForm({
      title: docDetail.title || '',
      abstract: docDetail.abstract || '',
      category: docDetail.category || 'Academic',
      tags: docDetail.tags || [],
      authors: docDetail.authors || '',
      year: docDetail.year || '',
      month: docDetail.month || '',
      institution: docDetail.institution || '',
      country: docDetail.country || '',
      external_url: docDetail.external_url || '',
      // When the doc currently has a file we expose a "Switch to URL" toggle
      // that the user must explicitly tick to convert. This avoids accidentally
      // blanking out an uploaded PDF just by saving the form.
      switch_to_url: false,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      // Build the payload: only send external_url when the user is genuinely
      // switching to URL mode (either already URL-only, or has toggled the
      // "Switch from file to URL" affordance on a file-backed doc).
      const isFileDoc = !!docDetail.original_filename;
      const payload = { ...editForm };
      delete payload.switch_to_url;
      const shouldSendUrl = !isFileDoc || editForm.switch_to_url;
      if (!shouldSendUrl) delete payload.external_url;
      else if (!editForm.external_url.trim()) {
        toast.error('Enter a URL to switch this entry from a file to a link');
        setSaving(false);
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/learning/${docDetail.doc_id}`, {
        method: 'PUT',
        ...fetchOpts,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const updated = await res.json();
        setDocDetail(prev => ({ ...prev, ...updated }));
        setEditing(false);
        toast.success(
          editForm.switch_to_url && isFileDoc
            ? 'Switched to URL — original file removed'
            : 'Research entry updated'
        );
        if (onUpdated) onUpdated(docDetail.doc_id, updated);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/learning/${docDetail.doc_id}/files`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: fd,
      });
      if (res.ok) {
        const newFile = await res.json();
        setDocDetail(prev => ({
          ...prev,
          additional_files: [...(prev.additional_files || []), newFile],
        }));
        toast.success(`File "${file.name}" uploaded`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const toggleEditTag = (t) => {
    setEditForm(prev => ({
      ...prev,
      tags: prev.tags.includes(t) ? prev.tags.filter(x => x !== t) : prev.tags.length < 6 ? [...prev.tags, t] : prev.tags,
    }));
  };

  const detailCatBg = docDetail.category === 'Academic' ? 'bg-brand-navy' : docDetail.category === 'Case Study' ? 'bg-amber-600' : 'bg-brand-sage';

  return (
    <div className="flex flex-col h-full max-h-[85vh]">
      {/* Header — when editing, switch to a scrollable container so the
          form never overflows the modal. `pr-12` reserves space for the
          floating close (X) button so long titles never collide with it. */}
      <div className={`p-6 pr-12 pb-4 border-b ${editing ? 'flex-1 overflow-y-auto min-h-0' : 'flex-shrink-0'}`}>
        {!editing ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white ${detailCatBg}`}>
                {docDetail.category}
              </span>
              {(docDetail.month || docDetail.year) && <span className="text-xs text-slate-400">{[docDetail.month, docDetail.year].filter(Boolean).join(' ')}</span>}
              {docDetail.status && docDetail.status !== 'approved' && (
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  docDetail.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }`}>{docDetail.status}</span>
              )}
              {docDetail.citation_count > 0 && (
                <span className="text-xs text-brand-navy font-medium flex items-center gap-1" data-testid="detail-citation-count">
                  <Quote className="h-3 w-3" /> {docDetail.citation_count} citation{docDetail.citation_count !== 1 ? 's' : ''}
                </span>
              )}
              {docDetail.read_count > 0 && (
                <span className="text-xs text-slate-500 font-medium flex items-center gap-1" data-testid="detail-read-count">
                  <Eye className="h-3 w-3" /> {docDetail.read_count} read{docDetail.read_count !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-xl font-bold text-slate-800 mb-1" data-testid="doc-detail-title">{docDetail.title}</h2>
            </div>
            {docDetail.authors && (
              <p className="text-sm text-slate-500 mb-3 flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{docDetail.authors}</p>
            )}

            {/* Rating */}
            <div className="flex items-center gap-4 mb-3" data-testid="detail-rating-section">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">Your rating:</span>
                <StarRating value={userRating} onChange={handleRate} size="md" />
              </div>
              <div className="h-5 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <StarRating value={avgRating} size="sm" />
                <span className="text-sm font-semibold text-amber-600">{avgRating > 0 ? avgRating : '—'}</span>
                <span className="text-xs text-slate-400">({ratingCount} rating{ratingCount !== 1 ? 's' : ''})</span>
              </div>
            </div>

            {/* Action bar */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => onBookmark(docDetail)} data-testid="detail-bookmark-btn"
                className={docDetail.is_bookmarked ? 'text-brand-navy border-brand-navy/30 bg-brand-navy/5' : ''}>
                {docDetail.is_bookmarked
                  ? <><BookmarkCheck className="h-4 w-4 mr-1.5 fill-brand-navy" /> Bookmarked</>
                  : <><Bookmark className="h-4 w-4 mr-1.5" /> Bookmark</>
                }
              </Button>
              <Button variant="outline" size="sm" onClick={() => onCite(docDetail)} data-testid="detail-cite-btn"
                className={docDetail.user_has_cited ? 'text-brand-sage border-brand-sage/30 bg-brand-sage/5' : ''}>
                <Copy className="h-4 w-4 mr-1.5" /> {docDetail.user_has_cited ? 'Cited' : 'Cite'}
              </Button>
              {docDetail.external_url && (
                <Button size="sm" className="bg-brand-navy text-white" data-testid="open-external-btn"
                  onClick={() => window.open(docDetail.external_url, '_blank', 'noopener,noreferrer')}>
                  <Download className="h-4 w-4 mr-1.5" /> Open Source
                </Button>
              )}
              {docDetail.original_filename && (
                <Button size="sm" className="bg-brand-navy text-white" data-testid="download-btn"
                  onClick={() => { triggerDownload(`${BACKEND_URL}/api/learning/${docDetail.doc_id}/download?auth=${sessionStorage.getItem('session_token') || ''}`, docDetail.original_filename); }}>
                  <Download className="h-4 w-4 mr-1.5" /> Download
                </Button>
              )}
              {canEdit && (
                <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-research-btn">
                  <Pencil className="h-4 w-4 mr-1.5" /> Edit
                </Button>
              )}
            </div>
          </>
        ) : (
          /* Edit mode — grouped sections, proper labels, generous spacing. */
          <div className="space-y-6 max-w-2xl" data-testid="edit-research-form">
            <div>
              <h3 className="text-base font-bold text-slate-800 mb-1">Edit research</h3>
              <p className="text-xs text-slate-500">Update the metadata below — only fields you change will be saved.</p>
            </div>

            {/* Title + abstract */}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Title</label>
                <Input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. The Collaboration Playbook" data-testid="edit-title" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Abstract / Summary</label>
                <RichTextEditor value={editForm.abstract} onChange={v => setEditForm(p => ({ ...p, abstract: v }))}
                  placeholder="A short summary of what readers will find inside…"
                  minHeight="7rem"
                  testId="edit-abstract" />
              </div>
            </div>

            {/* Classification */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Classification</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Category</label>
                  <select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="edit-category">
                    <option value="Academic">Academic</option>
                    <option value="Business">Business</option>
                    <option value="Case Study">Case Study</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Year</label>
                  <Input value={editForm.year} onChange={e => setEditForm(p => ({ ...p, year: e.target.value }))}
                    placeholder="2026" data-testid="edit-year" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Month</label>
                  <select value={editForm.month || ''} onChange={e => setEditForm(p => ({ ...p, month: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="edit-month">
                    <option value="">—</option>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Attribution */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Attribution</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Author(s)</label>
                  <Input value={editForm.authors} onChange={e => setEditForm(p => ({ ...p, authors: e.target.value }))}
                    placeholder="e.g. Jane Smith" data-testid="edit-authors" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Institution</label>
                  <Input value={editForm.institution || ''} onChange={e => setEditForm(p => ({ ...p, institution: e.target.value }))}
                    placeholder="e.g. ICW" data-testid="edit-institution" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Country</label>
                  <Input value={editForm.country || ''} onChange={e => setEditForm(p => ({ ...p, country: e.target.value }))}
                    placeholder="e.g. United Kingdom" data-testid="edit-country" />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Tags</p>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-slate-50 rounded-md border border-slate-100">
                {allTags.map(t => (
                  <button key={t} onClick={() => toggleEditTag(t)} type="button"
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      editForm.tags.includes(t)
                        ? 'bg-brand-navy text-white border-brand-navy'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40'
                    }`}>{t}</button>
                ))}
              </div>
            </div>

            {/* Source — keeps existing URL swap behaviour but cleaner. */}
            <div data-testid="edit-source-block">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Source</p>
              {!docDetail.original_filename ? (
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">External URL</label>
                  <Input value={editForm.external_url || ''} placeholder="https://…"
                    onChange={e => setEditForm(p => ({ ...p, external_url: e.target.value }))}
                    data-testid="edit-external-url" />
                  <p className="text-[11px] text-slate-400 mt-1">Members will be sent to this link when they tap Open Source.</p>
                </div>
              ) : (
                <div className="p-3 bg-amber-50/40 border border-amber-100 rounded-md">
                  <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                    <input type="checkbox" checked={!!editForm.switch_to_url}
                      onChange={e => setEditForm(p => ({ ...p, switch_to_url: e.target.checked }))}
                      data-testid="edit-switch-to-url-toggle" />
                    Replace the uploaded file with a URL instead
                  </label>
                  {editForm.switch_to_url && (
                    <div className="mt-3">
                      <Input value={editForm.external_url || ''} placeholder="https://…"
                        onChange={e => setEditForm(p => ({ ...p, external_url: e.target.value }))}
                        data-testid="edit-switch-url-input" />
                      <p className="text-[11px] text-amber-700 mt-1.5">
                        Saving will delete the file (<span className="font-medium">{docDetail.original_filename}</span>) and store this URL. Cannot be undone.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
              <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-brand-navy text-white" data-testid="save-edit-btn">
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs — hidden in edit mode so the edit form has the full modal height */}
      {!editing && (
      <div className="border-b flex-shrink-0 px-6">
        <div className="flex gap-4">
          {[
            { key: 'preview', label: 'Document', icon: Eye },
            { key: 'comments', label: `Comments (${comments.length})`, icon: MessageSquare },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-brand-navy text-brand-navy'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Tab Content */}
      {!editing && (
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === 'preview' && (
          <div className="p-6">
            {/* Abstract */}
            {docDetail.abstract && (
              <div className="mb-4">
                <RichText html={docDetail.abstract} className="text-sm text-slate-600 leading-relaxed" />
              </div>
            )}
            {docDetail.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {docDetail.tags.map(t => (
                  <Badge key={t} variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">{t}</Badge>
                ))}
              </div>
            )}
            {/* Main document preview */}
            {!docDetail.original_filename && docDetail.external_url ? (
              <div className="rounded-lg border bg-slate-50 p-8 text-center mb-4" data-testid="doc-preview-external">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-1">External resource</p>
                <p className="text-xs text-slate-400 mb-4 break-all">{docDetail.external_url}</p>
                <Button className="bg-brand-navy text-white" data-testid="open-external-btn-preview"
                  onClick={() => window.open(docDetail.external_url, '_blank', 'noopener,noreferrer')}>
                  <Download className="h-4 w-4 mr-1.5" /> Open Source
                </Button>
              </div>
            ) : isPdf ? (
              previewLoading ? (
                <div className="rounded-lg border bg-slate-100 flex items-center justify-center mb-4" style={{ height: '500px' }}>
                  <div className="text-center">
                    <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Loading document...</p>
                  </div>
                </div>
              ) : previewBlobUrl ? (
                <PdfViewer
                  blobUrl={previewBlobUrl}
                  downloadUrl={`${BACKEND_URL}/api/learning/${docDetail.doc_id}/download?auth=${sessionStorage.getItem('session_token') || ''}`}
                  viewUrl={`${BACKEND_URL}/api/learning/${docDetail.doc_id}/view?auth=${sessionStorage.getItem('session_token') || ''}`}
                  filename={docDetail.original_filename}
                />
              ) : (
                <div className="rounded-lg border bg-slate-50 p-8 text-center mb-4" data-testid="doc-preview-fallback">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 mb-1">Unable to preview this document</p>
                  <p className="text-xs text-slate-400 mb-4">Download the file to view it.</p>
                  <Button className="bg-brand-navy text-white"
                    onClick={() => { triggerDownload(`${BACKEND_URL}/api/learning/${docDetail.doc_id}/download?auth=${sessionStorage.getItem('session_token') || ''}`, docDetail.original_filename); }}>
                    <Download className="h-4 w-4 mr-1.5" /> Download File
                  </Button>
                </div>
              )
            ) : (
              <div className="rounded-lg border bg-slate-50 p-8 text-center mb-4" data-testid="doc-preview-fallback">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-1">
                  <strong>{docDetail.original_filename}</strong>
                </p>
                <p className="text-xs text-slate-400 mb-4">
                  This file type doesn't support inline preview. Download to view.
                </p>
                <Button className="bg-brand-navy text-white"
                  onClick={() => { triggerDownload(`${BACKEND_URL}/api/learning/${docDetail.doc_id}/download?auth=${sessionStorage.getItem('session_token') || ''}`, docDetail.original_filename); }}>
                  <Download className="h-4 w-4 mr-1.5" /> Download File
                </Button>
              </div>
            )}

            {/* Additional files */}
            {(docDetail.additional_files?.length > 0 || canEdit) && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Additional Files</h4>
                {docDetail.additional_files?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {docDetail.additional_files.map(f => (
                      <div key={f.file_id} className="flex items-center justify-between p-2.5 rounded-lg border bg-slate-50" data-testid={`additional-file-${f.file_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <span className="text-sm text-slate-700 truncate">{f.filename}</span>
                          {f.file_size && <span className="text-xs text-slate-400 flex-shrink-0">{formatFileSize(f.file_size)}</span>}
                        </div>
                        <Button variant="outline" size="sm" data-testid={`download-additional-${f.file_id}`}
                          onClick={() => { triggerDownload(`${BACKEND_URL}/api/learning/${docDetail.doc_id}/files/${f.file_id}/download?auth=${sessionStorage.getItem('session_token') || ''}`, f.filename); }}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {canEdit && (
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50 cursor-pointer transition-colors" data-testid="add-file-btn">
                    <Upload className="h-3.5 w-3.5" />
                    {uploading ? 'Uploading...' : 'Add File'}
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading}
                      accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.csv" />
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="p-6">
            {/* Add comment */}
            <div className="flex gap-3 mb-6" data-testid="comment-form">
              <Textarea
                placeholder="Share your thoughts on this research..."
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                className="flex-1 min-h-[80px] resize-none"
                data-testid="comment-input"
              />
              <Button
                onClick={handleComment}
                disabled={submitting || !commentText.trim()}
                className="self-end bg-brand-navy text-white"
                data-testid="submit-comment-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>

            {/* Comments list */}
            {loadingComments ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-brand-navy border-t-transparent rounded-full" />
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No comments yet. Be the first to share your thoughts!</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="comments-list">
                {comments.map(c => (
                  <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-navy/10 flex items-center justify-center overflow-hidden">
                      {c.user_picture ? (
                        <img src={c.user_picture} alt="" className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <User className="h-4 w-4 text-brand-navy" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-slate-700">{c.user_name}</span>
                        <span className="text-xs text-slate-400">{formatTimeAgo(c.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/* ── Main Page ── */
export default function ResearchLibraryPage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [allTags, setAllTags] = useState([]);
  const [hasInterests, setHasInterests] = useState(true);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [tag, setTag] = useState(searchParams.get('tag') || '');
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'bookmarks' | 'forYou'
  const [showFilters, setShowFilters] = useState(false);
  const [authorFilter, setAuthorFilter] = useState('');
  const [institutionFilter, setInstitutionFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');

  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadForm, setUploadForm] = useState({ title: '', abstract: '', category: 'Academic', tags: [], authors: '', year: '', month: '', institution: '', country: '', file: null, external_url: '', submission_type: 'file' });

  const [viewDoc, setViewDoc] = useState(null);

  const token = sessionStorage.getItem('session_token');
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const fetchOpts = { credentials: 'include', headers: { 'Content-Type': 'application/json', ...headers } };

  const loadDocs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      if (viewMode === 'bookmarks') {
        const res = await fetch(`${BACKEND_URL}/api/learning/my/bookmarks`, fetchOpts);
        if (res.ok) {
          const d = await res.json();
          setDocs(d.docs || []);
          setTotal(d.total || 0);
          setPage(1);
          setTotalPages(1);
        }
      } else {
        const params = new URLSearchParams({ page: p, limit: 12 });
        if (search) params.set('search', search);
        if (category) params.set('category', category);
        if (tag) params.set('tag', tag);
        if (viewMode === 'forYou') params.set('for_you', 'true');
        if (authorFilter) params.set('author', authorFilter);
        if (institutionFilter) params.set('institution', institutionFilter);
        if (countryFilter) params.set('country', countryFilter);
        if (yearFrom) params.set('year_from', yearFrom);
        if (yearTo) params.set('year_to', yearTo);

        const res = await fetch(`${BACKEND_URL}/api/learning?${params}`, fetchOpts);
        if (res.ok) {
          const d = await res.json();
          setDocs(d.docs || []);
          setTotal(d.total || 0);
          setPage(d.page || 1);
          setTotalPages(d.pages || 1);
          if (d.tags) setAllTags(d.tags);
          if (d.has_interests !== undefined) setHasInterests(d.has_interests);
        }
      }
    } catch {}
    setLoading(false);
  }, [search, category, tag, viewMode, authorFilter, institutionFilter, countryFilter, yearFrom, yearTo]);

  useEffect(() => { loadDocs(1); }, [search, category, tag, viewMode, authorFilter, institutionFilter, countryFilter, yearFrom, yearTo]);

  const handleBookmark = async (doc) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/learning/${doc.doc_id}/bookmark`, {
        method: 'POST', ...fetchOpts,
      });
      if (res.ok) {
        const data = await res.json();
        setDocs(prev => prev.map(d =>
          d.doc_id === doc.doc_id ? { ...d, is_bookmarked: data.bookmarked } : d
        ));
        if (viewDoc?.doc_id === doc.doc_id) {
          setViewDoc(prev => prev ? { ...prev, is_bookmarked: data.bookmarked } : prev);
        }
        toast.success(data.bookmarked ? 'Bookmarked!' : 'Bookmark removed');
        if (viewMode === 'bookmarks' && !data.bookmarked) {
          setDocs(prev => prev.filter(d => d.doc_id !== doc.doc_id));
        }
      }
    } catch { toast.error('Failed to bookmark'); }
  };

  const handleCite = async (doc) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/learning/${doc.doc_id}/cite`, {
        method: 'POST', ...fetchOpts,
      });
      if (res.ok) {
        const data = await res.json();
        setDocs(prev => prev.map(d =>
          d.doc_id === doc.doc_id ? { ...d, citation_count: data.citation_count } : d
        ));
        if (viewDoc?.doc_id === doc.doc_id) {
          setViewDoc(prev => prev ? { ...prev, citation_count: data.citation_count, user_has_cited: true } : prev);
        }
        const citationText = `${doc.authors || 'Unknown'}${doc.year ? ` (${doc.year})` : ''}. "${doc.title}". SLL Learning.`;
        navigator.clipboard.writeText(citationText).then(() => {
          toast.success('Citation copied to clipboard!');
        }).catch(() => {
          toast.success('Citation recorded!');
        });
      }
    } catch { toast.error('Failed to cite'); }
  };

  const handleUpload = async () => {
    if (!uploadForm.title.trim()) { toast.error('Title is required'); return; }
    const isUrlMode = uploadForm.submission_type === 'url';
    if (isUrlMode) {
      const u = (uploadForm.external_url || '').trim();
      if (!u || !/^https?:\/\//i.test(u)) { toast.error('Enter a valid http(s):// URL'); return; }
    } else {
      if (!uploadForm.file) { toast.error('Please select a file'); return; }
    }
    if (uploadForm.tags.length === 0) { toast.error('Select at least one tag'); return; }

    // Anything bigger than 5 MB is uploaded in 5 MB chunks so the
    // request never trips ingress / proxy body-size limits. Smaller
    // files keep the simpler one-shot multipart path.
    const CHUNK_THRESHOLD = 5 * 1024 * 1024;
    const CHUNK_SIZE = 5 * 1024 * 1024;
    const SIZE_CEILING = 250 * 1024 * 1024;
    if (!isUrlMode && uploadForm.file && uploadForm.file.size > SIZE_CEILING) {
      toast.error(`File too large. Max ${SIZE_CEILING / (1024 * 1024)} MB.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      let res;
      if (isUrlMode) {
        res = await fetch(`${BACKEND_URL}/api/learning/link`, {
          method: 'POST', credentials: 'include',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: uploadForm.title,
            abstract: uploadForm.abstract,
            category: uploadForm.category,
            tags: uploadForm.tags,
            authors: uploadForm.authors,
            year: uploadForm.year || undefined,
            month: uploadForm.month || undefined,
            institution: uploadForm.institution || undefined,
            country: uploadForm.country || undefined,
            external_url: uploadForm.external_url.trim(),
          }),
        });
      } else if (uploadForm.file.size > CHUNK_THRESHOLD) {
        // ── Chunked upload path ──
        const init = await fetch(`${BACKEND_URL}/api/learning/upload-init`, {
          method: 'POST', credentials: 'include',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: uploadForm.file.name, total_size: uploadForm.file.size }),
        });
        if (!init.ok) {
          const err = await init.json().catch(() => ({}));
          throw new Error(err.detail || 'Upload init failed');
        }
        const { upload_id } = await init.json();

        const totalChunks = Math.ceil(uploadForm.file.size / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, uploadForm.file.size);
          const chunk = uploadForm.file.slice(start, end);
          const fd = new FormData();
          fd.append('chunk', chunk, `${uploadForm.file.name}.part${i}`);
          fd.append('index', String(i));
          const cRes = await fetch(`${BACKEND_URL}/api/learning/upload-chunk/${upload_id}`, {
            method: 'POST', credentials: 'include', headers, body: fd,
          });
          if (!cRes.ok) {
            const err = await cRes.json().catch(() => ({}));
            throw new Error(err.detail || `Chunk ${i + 1} failed`);
          }
          setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
        }

        res = await fetch(`${BACKEND_URL}/api/learning/upload-finalize/${upload_id}`, {
          method: 'POST', credentials: 'include',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: uploadForm.title,
            abstract: uploadForm.abstract,
            category: uploadForm.category,
            tags: uploadForm.tags,
            authors: uploadForm.authors,
            year: uploadForm.year || undefined,
            month: uploadForm.month || undefined,
            institution: uploadForm.institution || undefined,
            country: uploadForm.country || undefined,
            content_type: uploadForm.file.type || 'application/octet-stream',
          }),
        });
      } else {
        // ── One-shot multipart path (small files) ──
        const fd = new FormData();
        fd.append('file', uploadForm.file);
        fd.append('title', uploadForm.title);
        fd.append('abstract', uploadForm.abstract);
        fd.append('category', uploadForm.category);
        fd.append('tags', uploadForm.tags.join(','));
        fd.append('authors', uploadForm.authors);
        if (uploadForm.year) fd.append('year', uploadForm.year);
        if (uploadForm.month) fd.append('month', uploadForm.month);
        if (uploadForm.institution) fd.append('institution', uploadForm.institution);
        if (uploadForm.country) fd.append('country', uploadForm.country);

        res = await fetch(`${BACKEND_URL}/api/learning/upload`, {
          method: 'POST', credentials: 'include', headers, body: fd,
        });
      }
      if (res.ok) {
        toast.success(isUrlMode
          ? 'Learning link submitted for review!'
          : 'Document submitted for review! An admin will review it shortly.');
        setShowUpload(false);
        setUploadForm({ title: '', abstract: '', category: 'Academic', tags: [], authors: '', year: '', month: '', file: null, external_url: '', submission_type: 'file', institution: '', country: '' });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch (e) {
      toast.error(e.message || 'Submission failed');
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handleRated = (docId, newAvg, newCount) => {
    setDocs(prev => prev.map(d =>
      d.doc_id === docId ? { ...d, avg_rating: newAvg, rating_count: newCount } : d
    ));
  };

  const toggleTag = (t) => {
    setUploadForm(prev => ({
      ...prev,
      tags: prev.tags.includes(t) ? prev.tags.filter(x => x !== t) : prev.tags.length < 6 ? [...prev.tags, t] : prev.tags,
    }));
  };

  const setFilter = (key, val, setter) => {
    setter(val);
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="learning" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold font-heading gradient-text flex items-center gap-2" data-testid="learning-title">Learning <PageExplainer pageKey="learning" label="About Research" /></h1>
              <p className="text-slate-500 mt-1">Academic papers & business reports for the sustainability community</p>
            </div>
            <Button onClick={() => setShowUpload(true)} data-testid="upload-research-btn"
              className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
              <Upload className="h-4 w-4 mr-1.5" /> Submit Research
            </Button>
          </div>

          {/* View mode + Category Tabs + Search */}
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex gap-2">
                <Button variant={viewMode === 'all' && !category ? 'default' : 'outline'} size="sm"
                  onClick={() => { setViewMode('all'); setFilter('category', '', setCategory); }}
                  className={viewMode === 'all' && !category ? 'bg-brand-navy text-white' : ''} data-testid="filter-all">All</Button>
                <Button variant={viewMode === 'forYou' ? 'default' : 'outline'} size="sm"
                  onClick={() => setViewMode(viewMode === 'forYou' ? 'all' : 'forYou')}
                  className={viewMode === 'forYou' ? 'bg-brand-navy text-white' : ''} data-testid="filter-for-you">
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> For You
                </Button>
                <Button variant={category === 'Academic' ? 'default' : 'outline'} size="sm"
                  onClick={() => { setViewMode('all'); setFilter('category', 'Academic', setCategory); }}
                  className={category === 'Academic' ? 'bg-brand-navy text-white' : ''} data-testid="filter-academic">
                  <BookOpen className="h-3.5 w-3.5 mr-1" /> Academic
                </Button>
                <Button variant={category === 'Business' ? 'default' : 'outline'} size="sm"
                  onClick={() => { setViewMode('all'); setFilter('category', 'Business', setCategory); }}
                  className={category === 'Business' ? 'bg-brand-sage text-white' : ''} data-testid="filter-business">
                  <Briefcase className="h-3.5 w-3.5 mr-1" /> Business
                </Button>
                <Button variant={category === 'Case Study' ? 'default' : 'outline'} size="sm"
                  onClick={() => { setViewMode('all'); setFilter('category', 'Case Study', setCategory); }}
                  className={category === 'Case Study' ? 'bg-amber-600 text-white' : ''} data-testid="filter-case-study">
                  <FileSearch className="h-3.5 w-3.5 mr-1" /> Case Studies
                </Button>
                <div className="w-px bg-slate-200 mx-1" />
                <Button variant={viewMode === 'bookmarks' ? 'default' : 'outline'} size="sm"
                  onClick={() => setViewMode(viewMode === 'bookmarks' ? 'all' : 'bookmarks')}
                  className={viewMode === 'bookmarks' ? 'bg-brand-navy text-white' : ''} data-testid="filter-bookmarks">
                  <BookmarkCheck className="h-3.5 w-3.5 mr-1" /> My Bookmarks
                </Button>
              </div>
              {viewMode !== 'bookmarks' && (
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input placeholder="Search by title, author, abstract..."
                    value={search} onChange={e => { setSearch(e.target.value); const p = new URLSearchParams(searchParams); if (e.target.value) p.set('search', e.target.value); else p.delete('search'); setSearchParams(p); }}
                    className="pl-10" data-testid="learning-search" />
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-brand-navy text-white' : ''} data-testid="toggle-advanced-filters">
                <Filter className="h-3.5 w-3.5 mr-1" /> Filters
                {(authorFilter || institutionFilter || countryFilter || yearFrom || yearTo) && (
                  <span className="ml-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                    {[authorFilter, institutionFilter, countryFilter, yearFrom, yearTo].filter(Boolean).length}
                  </span>
                )}
              </Button>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && viewMode !== 'bookmarks' && (
              <div className="p-4 bg-slate-50 border rounded-lg" data-testid="advanced-filters-panel">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Tag</Label>
                    <select value={tag} onChange={e => setFilter('tag', e.target.value, setTag)}
                      data-testid="tag-filter-select"
                      className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm bg-white text-slate-700">
                      <option value="">All tags</option>
                      {allTags.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Professor / Author</Label>
                    <Input placeholder="e.g. Smith" value={authorFilter}
                      onChange={e => setAuthorFilter(e.target.value)}
                      className="h-8 text-sm" data-testid="filter-author" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Institute</Label>
                    <Input placeholder="e.g. Oxford" value={institutionFilter}
                      onChange={e => setInstitutionFilter(e.target.value)}
                      className="h-8 text-sm" data-testid="filter-institution" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Country</Label>
                    <Input placeholder="e.g. United Kingdom" value={countryFilter}
                      onChange={e => setCountryFilter(e.target.value)}
                      className="h-8 text-sm" data-testid="filter-country" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Year From</Label>
                    <Input placeholder="e.g. 2020" value={yearFrom}
                      onChange={e => setYearFrom(e.target.value)}
                      className="h-8 text-sm" data-testid="filter-year-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Year To</Label>
                    <Input placeholder="e.g. 2026" value={yearTo}
                      onChange={e => setYearTo(e.target.value)}
                      className="h-8 text-sm" data-testid="filter-year-to" />
                  </div>
                </div>
                {(authorFilter || institutionFilter || countryFilter || yearFrom || yearTo) && (
                  <Button variant="ghost" size="sm" className="mt-2 text-xs text-red-500" data-testid="clear-advanced-filters"
                    onClick={() => { setAuthorFilter(''); setInstitutionFilter(''); setCountryFilter(''); setYearFrom(''); setYearTo(''); }}>
                    <X className="h-3 w-3 mr-1" /> Clear all filters
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Tag filter bar */}
          {tag && viewMode !== 'bookmarks' && (
            <div className="flex items-center gap-2 mb-4">
              <Tag className="h-4 w-4 text-slate-400" />
              <Badge variant="outline" className="bg-brand-navy/5 text-brand-navy border-brand-navy/20 gap-1.5">
                {tag}
                <button onClick={() => setFilter('tag', '', setTag)}><X className="h-3 w-3" /></button>
              </Badge>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Main content */}
            <div className="flex-1">
              <p className="text-sm text-slate-500 mb-4" data-testid="results-count">
                {viewMode === 'forYou'
                  ? (hasInterests
                    ? `${total} recommended document${total !== 1 ? 's' : ''} based on your interests`
                    : `Update your interests in your profile to get personalised recommendations`)
                  : viewMode === 'bookmarks' ? `${total} bookmarked document${total !== 1 ? 's' : ''}`
                  : `${total} document${total !== 1 ? 's' : ''}`}
              </p>

              {loading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
                </div>
              ) : docs.length === 0 ? (
                <Card className="p-12 text-center">
                  {viewMode === 'forYou' ? (
                    <>
                      <Sparkles className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No recommendations yet</p>
                      <p className="text-sm text-slate-400 mt-1">Update your Forum Topics of Interest in your profile to get personalised research recommendations</p>
                    </>
                  ) : viewMode === 'bookmarks' ? (
                    <>
                      <Bookmark className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No bookmarked documents yet</p>
                      <p className="text-sm text-slate-400 mt-1">Bookmark research papers to save them here for quick access</p>
                    </>
                  ) : (
                    <>
                      <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No research documents found</p>
                    </>
                  )}
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr" data-testid="learning-grid">
                  {docs.map(d => <DocCard key={d.doc_id} doc={d} onView={setViewDoc} onBookmark={handleBookmark} />)}
                </div>
              )}

              {viewMode !== 'bookmarks' && totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => loadDocs(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => loadDocs(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          </div>
          <QuickActions user={user} exclude={['/learning']} />
            </div>
            <PageSideRail placement="learning" below={<div className="space-y-4"><NewsHeadlines /><SuggestedFollows meId={user?.user_id} /></div>} />
          </div>
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Submit Research Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              Documents are reviewed by an admin before being published.
            </div>
            <Input placeholder="Title *" value={uploadForm.title}
              onChange={e => setUploadForm(p => ({ ...p, title: e.target.value }))} data-testid="upload-title" />
            <RichTextEditor placeholder="Abstract / Summary" value={uploadForm.abstract}
              onChange={v => setUploadForm(p => ({ ...p, abstract: v }))} minHeight="6rem" testId="upload-abstract" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Category *</label>
                <select value={uploadForm.category} onChange={e => setUploadForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="upload-category">
                  <option value="Academic">Academic</option>
                  <option value="Business">Business</option>
                  <option value="Case Study">Case Study</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Year</label>
                <Input placeholder="e.g. 2025" value={uploadForm.year}
                  onChange={e => setUploadForm(p => ({ ...p, year: e.target.value }))} data-testid="upload-year" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Month</label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  value={uploadForm.month} onChange={e => setUploadForm(p => ({ ...p, month: e.target.value }))} data-testid="upload-month">
                  <option value="">-- Optional --</option>
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
            <Input placeholder="Author(s)" value={uploadForm.authors}
              onChange={e => setUploadForm(p => ({ ...p, authors: e.target.value }))} data-testid="upload-authors" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Institution (e.g. University of Oxford)" value={uploadForm.institution}
                onChange={e => setUploadForm(p => ({ ...p, institution: e.target.value }))} data-testid="upload-institution" />
              <Input placeholder="Country (e.g. United Kingdom)" value={uploadForm.country}
                onChange={e => setUploadForm(p => ({ ...p, country: e.target.value }))} data-testid="upload-country" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Tags * (select up to 6)</label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="upload-tags">
                {allTags.map(t => (
                  <button key={t} onClick={() => toggleTag(t)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      uploadForm.tags.includes(t) ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40'
                    }`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1.5 block">How would you like to submit this research?</label>
              <div className="grid grid-cols-2 gap-2" data-testid="submission-type-toggle">
                <button
                  type="button"
                  onClick={() => setUploadForm(p => ({ ...p, submission_type: 'file' }))}
                  data-testid="submission-type-file"
                  className={`text-sm py-2.5 px-3 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                    uploadForm.submission_type === 'file'
                      ? 'border-brand-navy bg-brand-navy/5 text-brand-navy font-semibold'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}>
                  <FileText className="h-4 w-4" /> Upload file
                </button>
                <button
                  type="button"
                  onClick={() => setUploadForm(p => ({ ...p, submission_type: 'url' }))}
                  data-testid="submission-type-url"
                  className={`text-sm py-2.5 px-3 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                    uploadForm.submission_type === 'url'
                      ? 'border-brand-navy bg-brand-navy/5 text-brand-navy font-semibold'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}>
                  <ExternalLink className="h-4 w-4" /> Link to URL
                </button>
              </div>
            </div>

            {uploadForm.submission_type === 'file' ? (
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">File * (PDF, DOCX, XLSX, PPTX, TXT, CSV — max 10MB)</label>
                <Input type="file" onChange={e => setUploadForm(p => ({ ...p, file: e.target.files?.[0] || null }))}
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.csv" data-testid="upload-file" />
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">URL *</label>
                <Input type="url" placeholder="https://example.com/paper.pdf"
                  value={uploadForm.external_url}
                  onChange={e => setUploadForm(p => ({ ...p, external_url: e.target.value }))}
                  data-testid="upload-url" />
                <p className="text-[11px] text-slate-400 mt-1">Use this for paywalled, hosted-elsewhere or licensed research that can't be uploaded directly. Members will click through to the source URL.</p>
              </div>
            )}
            {uploading && uploadProgress > 0 && (
              <div data-testid="upload-progress" className="space-y-1">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Uploading large file in chunks…</span>
                  <span className="tabular-nums">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-navy transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
            <Button onClick={handleUpload} disabled={uploading} className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white"
              data-testid="submit-upload-btn">
              {uploading ? (uploadProgress > 0 ? `Uploading… ${uploadProgress}%` : 'Uploading...') : 'Submit for Review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Detail Dialog — expanded with preview, rating, comments */}
      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
        <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden" data-testid="doc-detail-dialog"
          style={{ maxHeight: '90vh' }} aria-describedby={undefined}>
          <DialogHeader className="sr-only"><DialogTitle>{viewDoc?.title || 'Research Document'}</DialogTitle></DialogHeader>
          {viewDoc && (
            <ResearchDetail
              doc={viewDoc}
              user={user}
              token={token}
              headers={headers}
              fetchOpts={fetchOpts}
              onClose={() => setViewDoc(null)}
              onBookmark={handleBookmark}
              onCite={handleCite}
              onRated={handleRated}
              onUpdated={(docId, updated) => {
                setDocs(prev => prev.map(d => d.doc_id === docId ? { ...d, ...updated } : d));
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
